import React from 'react';

import { CELL_WIDTHS } from '@/components/table/constants';
import { Column } from '@/components/table/types';

/**
 * Options for applying column width styles
 */
export interface WidthApplicationOptions {
  /** Whether column is resizable by user */
  resize?: boolean;
  /** User-resized width stored in localStorage */
  storedWidth?: number;
  /** Whether table uses tableLayout: 'fixed' */
  tableLayoutFixed?: boolean;
  /** Whether responsive column hiding is active */
  responsiveMode?: boolean;
}

/**
 * Applies column width configuration to CSS style object.
 * Single source of truth for width application across col/Th/Td.
 *
 * CRITICAL: This function ensures isExpandable.minWidth is applied to ALL
 * rendering layers (colgroup, Th, Td), not just header span.
 *
 * @param column - Column configuration
 * @param options - Context-specific options
 * @returns CSS properties for width constraints
 */
export function applyColumnWidth<TData>(
  column: Column<TData>,
  options: WidthApplicationOptions = {}
): React.CSSProperties {
  // Handle date columns specially (maintains existing behavior)
  if (column.cell === 'date') {
    const dateWidth = column.fixedWidth || CELL_WIDTHS.date;
    return {
      width: dateWidth,
      minWidth: dateWidth,
      maxWidth: dateWidth,
    };
  }

  // Determine effective width constraints
  const fixedWidth = column.fixedWidth;

  // CRITICAL FIX: Resolve minWidth from multiple sources
  // Priority: isExpandable.minWidth > column.minWidth
  const effectiveMinWidth = column.isExpandable?.minWidth || column.minWidth;
  const effectiveMaxWidth = column.maxWidth;

  // Responsive mode overrides stored widths to allow dynamic expansion
  const useStoredWidth = !options.responsiveMode && options.storedWidth;

  // Fixed width columns: Set exact width on all properties
  if (fixedWidth) {
    return {
      width: fixedWidth,
      minWidth: fixedWidth,
      maxWidth: fixedWidth,
      flexShrink: 0, // Prevent compression below minWidth
      flexGrow: 0, // Prevent expansion - stay at exact fixedWidth
    };
  }

  // Flexible/Expandable columns: Apply constraints
  const style: React.CSSProperties = {};

  if (effectiveMinWidth) {
    style.minWidth = effectiveMinWidth;
  }

  if (effectiveMaxWidth) {
    style.maxWidth = effectiveMaxWidth;
  }

  // Allow expandable columns to grow, prevent others from growing
  if (column.isExpandable) {
    style.flexGrow = 1; // Expandable columns fill available space
    style.flexShrink = 1; // But don't shrink below minWidth
  } else {
    style.flexGrow = 0; // Non-expandable stay at minWidth
    style.flexShrink = 0; // Don't compress
  }

  if (useStoredWidth) {
    // Validate stored width respects constraints
    let validatedWidth = options.storedWidth!;

    if (effectiveMinWidth && validatedWidth < effectiveMinWidth) {
      validatedWidth = effectiveMinWidth;
    }

    if (effectiveMaxWidth && validatedWidth > effectiveMaxWidth) {
      validatedWidth = effectiveMaxWidth;
    }

    style.width = validatedWidth;
  } else if (options.tableLayoutFixed && effectiveMinWidth) {
    // tableLayout: 'fixed' requires explicit width on col elements.
    //
    // The handling here is conditional on responsive mode:
    //
    // - Responsive mode (columns hidden): set width = minWidth so the browser
    //   cannot compress columns below their minWidth. This is the original
    //   CRITICAL FIX — without an explicit width, tableLayout: 'fixed' can
    //   auto-distribute space and violate minWidth constraints. In this mode
    //   the table also gets a minTableWidth floor (Table.tsx), so the explicit
    //   width composes with that floor rather than over-constraining layout.
    //
    // - Non-responsive mode: leave isExpandable columns WITHOUT an explicit
    //   width so their <col> becomes width:auto and absorbs leftover viewport
    //   width. This lets every fixed-width column render at its true spec
    //   instead of the browser proportionally inflating all columns. The
    //   compression bug above only manifests in responsive mode, so this is
    //   safe. minWidth is still set above and keeps the absorber from
    //   collapsing too far.
    if (options.responsiveMode || !column.isExpandable) {
      style.width = effectiveMinWidth;
    }
  }

  return style;
}

/**
 * Validates column width constraints and logs warnings for invalid configurations.
 * Call this during development to catch configuration errors.
 *
 * @param column - Column configuration to validate
 */
export function validateColumnWidth<TData>(column: Column<TData>): void {
  const { fixedWidth, minWidth, maxWidth, isExpandable, label } = column;

  // Check for conflicting fixedWidth + flexible properties
  if (fixedWidth && (minWidth || maxWidth || isExpandable)) {
    console.warn(
      `Column "${label}": fixedWidth is set but also has minWidth/maxWidth/isExpandable. ` +
        `fixedWidth takes precedence, other properties are ignored.`
    );
  }

  // Check for minWidth > maxWidth
  if (minWidth && maxWidth && minWidth > maxWidth) {
    console.error(
      `Column "${label}": minWidth (${minWidth}) is greater than maxWidth (${maxWidth}). ` +
        `This is invalid and will cause rendering issues.`
    );
  }

  // Check for duplicate minWidth specifications
  if (isExpandable && minWidth && isExpandable.minWidth !== minWidth) {
    console.warn(
      `Column "${label}": Both isExpandable.minWidth (${isExpandable.minWidth}) and ` +
        `column.minWidth (${minWidth}) are set with different values. ` +
        `isExpandable.minWidth takes precedence.`
    );
  }
}

/**
 * Calculates total required width for visible columns.
 * Used by responsive column logic to determine which columns to hide.
 *
 * @param columns - Array of column configurations
 * @returns Total minimum width required in pixels
 */
export function calculateTotalRequiredWidth<TData>(
  columns: Column<TData>[]
): number {
  return columns.reduce((total, column) => {
    // Get minimum width (what column cannot shrink below)
    const minWidth =
      column.fixedWidth ||
      column.isExpandable?.minWidth ||
      column.minWidth ||
      100; // Default minimum

    return total + minWidth;
  }, 0);
}
